import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Navigation, User, Phone, MessageSquare, Shield, Share2, AlertTriangle, ChevronRight, MapPin, X } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { supabase } from '@/integrations/supabase/client';
import { calculateDistance } from '@/utils/distanceCalculation';
import { appendRideChatMessage, parseRideChatNotes, type RideChatMessage } from '@/utils/rideChat';
import { useToast } from '@/hooks/use-toast';

interface ActiveRideData {
  id: string;
  customer_id: string;
  pickup_address: string;
  dropoff_address: string;
  pickup_latitude: number;
  pickup_longitude: number;
  dropoff_latitude: number;
  dropoff_longitude: number;
  estimated_fare: number;
  distance_km: number;
  status: string;
  notes?: string | null;
}

interface ActiveRideNavigationProps {
  ride: ActiveRideData;
  driverLocation: { lat: number; lng: number } | null;
  onComplete: () => void;
  onCancel: () => void;
}

const ROUTE_REFRESH_MS = 12000;
const ROUTE_REFRESH_DISTANCE_KM = 0.12;

const ActiveRideNavigation = ({ ride, driverLocation, onComplete, onCancel }: ActiveRideNavigationProps) => {
  const { toast } = useToast();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const driverMarkerRef = useRef<L.Marker | null>(null);
  const routePolylineRef = useRef<L.Polyline | null>(null);
  const destMarkerRef = useRef<L.Marker | null>(null);
  const lastRouteSourceRef = useRef<{ lat: number; lng: number } | null>(null);
  const lastRouteFetchAtRef = useRef(0);
  const fitDoneRef = useRef(false);
  const phaseRef = useRef<'pickup' | 'dropoff'>(ride.status === 'in_progress' ? 'dropoff' : 'pickup');

  const [customerName, setCustomerName] = useState('Customer');
  const [customerPhone, setCustomerPhone] = useState<string | null>(null);
  const [customerAvatar, setCustomerAvatar] = useState<string | null>(null);
  const [phase, setPhase] = useState<'pickup' | 'dropoff'>(ride.status === 'in_progress' ? 'dropoff' : 'pickup');
  const [etaMinutes, setEtaMinutes] = useState<number | null>(null);
  const [distanceToTarget, setDistanceToTarget] = useState<number | null>(null);
  const [currentInstruction, setCurrentInstruction] = useState('Head to pickup');
  const [showSafety, setShowSafety] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<RideChatMessage[]>(parseRideChatNotes(ride.notes));
  const [chatDraft, setChatDraft] = useState('');
  const [rawRideNotes, setRawRideNotes] = useState<string | null>(ride.notes ?? null);

  const targetCoords = phase === 'pickup'
    ? { lat: ride.pickup_latitude, lng: ride.pickup_longitude }
    : { lat: ride.dropoff_latitude, lng: ride.dropoff_longitude };

  const distanceText = distanceToTarget
    ? distanceToTarget < 1
      ? `${Math.round(distanceToTarget * 1000)}m`
      : `${distanceToTarget.toFixed(1)} km`
    : '—';

  useEffect(() => {
    const fetchCustomer = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('full_name, phone, avatar_url')
        .eq('id', ride.customer_id)
        .maybeSingle();

      if (data?.full_name) setCustomerName(data.full_name);
      if (data?.phone) setCustomerPhone(data.phone);
      if (data?.avatar_url) setCustomerAvatar(data.avatar_url);
    };

    fetchCustomer();
  }, [ride.customer_id]);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const center = driverLocation || targetCoords;

    const map = new L.Map(mapRef.current, {
      center: [center.lat, center.lng],
      zoom: 15,
      zoomControl: false,
      preferCanvas: true,
    });

    new L.TileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '© OpenStreetMap © CARTO',
      subdomains: 'abcd',
      maxZoom: 20,
    }).addTo(map);

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    const applyRideUpdate = (updatedRide: any) => {
      if (!updatedRide) return;

      if (updatedRide.status === 'in_progress') {
        setPhase('dropoff');
        fitDoneRef.current = false;
      }

      if (updatedRide.status === 'completed') {
        onComplete();
        return;
      }

      if (updatedRide.status === 'cancelled') {
        onCancel();
        return;
      }

      const updatedNotes = updatedRide.notes as string | null;
      setRawRideNotes(updatedNotes);
      setChatMessages(parseRideChatNotes(updatedNotes));
    };

    const rideChannel = supabase
      .channel(`active-ride-${ride.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'ride_requests',
        filter: `id=eq.${ride.id}`,
      }, (payload) => {
        applyRideUpdate(payload.new as any);
      })
      .subscribe();

    const pollInterval = setInterval(async () => {
      const { data: latestRide } = await supabase
        .from('ride_requests')
        .select('status, notes')
        .eq('id', ride.id)
        .maybeSingle();

      applyRideUpdate(latestRide);
    }, 2000);

    return () => {
      clearInterval(pollInterval);
      supabase.removeChannel(rideChannel);
    };
  }, [ride.id, onCancel, onComplete]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !driverLocation) return;

    const toTargetDistance = calculateDistance(driverLocation.lat, driverLocation.lng, targetCoords.lat, targetCoords.lng);
    setDistanceToTarget(toTargetDistance);

    const fallbackEta = Math.max(1, Math.round((toTargetDistance / 28) * 60));
    setEtaMinutes((prev) => {
      if (prev === null) return fallbackEta;
      return Math.max(1, Math.round(prev * 0.65 + fallbackEta * 0.35));
    });

    const driverIcon = L.divIcon({
      html: `<div style="width:20px;height:20px;border-radius:999px;background:hsl(var(--primary));border:3px solid hsl(var(--background));box-shadow:0 8px 20px hsl(var(--primary) / 0.45)"></div>`,
      iconSize: [26, 26],
      iconAnchor: [13, 13],
      className: '',
    });

    if (driverMarkerRef.current) {
      driverMarkerRef.current.setLatLng([driverLocation.lat, driverLocation.lng]);
    } else {
      driverMarkerRef.current = L.marker([driverLocation.lat, driverLocation.lng], { icon: driverIcon }).addTo(map);
    }

    // Proper pin markers for pickup/dropoff instead of dots
    const createPinIcon = (color: string) => L.divIcon({
      html: `<div style="display:flex;flex-direction:column;align-items:center;"><div style="width:14px;height:14px;background:${color};border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);"></div><div style="width:2px;height:16px;background:${color};border-radius:0 0 2px 2px;margin-top:-1px;"></div></div>`,
      className: '',
      iconSize: [20, 32],
      iconAnchor: [10, 32],
    });

    const destinationIcon = createPinIcon(phase === 'pickup' ? '#3b82f6' : '#f97316');

    if (destMarkerRef.current) {
      destMarkerRef.current.setLatLng([targetCoords.lat, targetCoords.lng]);
      destMarkerRef.current.setIcon(destinationIcon);
    } else {
      destMarkerRef.current = L.marker([targetCoords.lat, targetCoords.lng], { icon: destinationIcon }).addTo(map);
    }

    const movedDistance = lastRouteSourceRef.current
      ? calculateDistance(lastRouteSourceRef.current.lat, lastRouteSourceRef.current.lng, driverLocation.lat, driverLocation.lng)
      : Number.POSITIVE_INFINITY;

    const now = Date.now();
    const shouldRefreshRoute =
      !routePolylineRef.current ||
      movedDistance >= ROUTE_REFRESH_DISTANCE_KM ||
      now - lastRouteFetchAtRef.current > ROUTE_REFRESH_MS;

    if (!shouldRefreshRoute) return;

    let isDisposed = false;

    const drawRoute = async () => {
      try {
        const routeUrl = `https://router.project-osrm.org/route/v1/driving/${driverLocation.lng},${driverLocation.lat};${targetCoords.lng},${targetCoords.lat}?overview=full&geometries=geojson&steps=true`;
        const response = await fetch(routeUrl);
        const routeData = await response.json();
        if (isDisposed) return;

        const route = routeData?.routes?.[0];
        const coordinates = route?.geometry?.coordinates as [number, number][] | undefined;

        if (route?.distance) {
          setDistanceToTarget(route.distance / 1000);
        }

        if (route?.duration) {
          const nextEta = Math.max(1, Math.round(route.duration / 60));
          setEtaMinutes((prev) => {
            if (prev === null) return nextEta;
            return Math.max(1, Math.round(prev * 0.6 + nextEta * 0.4));
          });
        }

        const nextStep = route?.legs?.[0]?.steps?.[1] ?? route?.legs?.[0]?.steps?.[0];
        if (nextStep) {
          const maneuverType = nextStep?.maneuver?.type;
          const modifier = nextStep?.maneuver?.modifier;
          let instruction = phase === 'pickup' ? `Heading to pick up ${customerName}` : `Heading to drop off ${customerName}`;

          if (maneuverType === 'turn' && modifier === 'left') instruction = 'Turn left ahead';
          else if (maneuverType === 'turn' && modifier === 'right') instruction = 'Turn right ahead';
          else if (maneuverType === 'turn' && modifier === 'slight left') instruction = 'Slight left ahead';
          else if (maneuverType === 'turn' && modifier === 'slight right') instruction = 'Slight right ahead';
          else if (maneuverType === 'arrive') instruction = phase === 'pickup' ? 'Arriving at pickup' : 'Arriving at destination';
          else if (nextStep?.name) instruction = `Continue on ${nextStep.name}`;

          setCurrentInstruction(instruction);
        }

        if (coordinates && coordinates.length > 1) {
          const latLngs: L.LatLngExpression[] = coordinates.map(([lng, lat]) => [lat, lng]);

          if (routePolylineRef.current) {
            routePolylineRef.current.setLatLngs(latLngs);
          } else {
            routePolylineRef.current = L.polyline(latLngs, {
              color: 'hsl(var(--primary))',
              weight: 6,
              opacity: 0.9,
            }).addTo(map);
          }

          if (!fitDoneRef.current) {
            fitDoneRef.current = true;
            map.fitBounds(L.latLngBounds(latLngs), { padding: [80, 120] });
          }
        }

        lastRouteSourceRef.current = { ...driverLocation };
        lastRouteFetchAtRef.current = Date.now();
      } catch {
        setCurrentInstruction(phase === 'pickup' ? 'Head to pickup' : 'Head to destination');
      }
    };

    drawRoute();

    return () => {
      isDisposed = true;
    };
  }, [driverLocation, phase, targetCoords.lat, targetCoords.lng, customerName]);

  useEffect(() => {
    fitDoneRef.current = false;
  }, [phase]);

  const handlePickedUp = async () => {
    await supabase
      .from('ride_requests')
      .update({ status: 'in_progress' as any, started_at: new Date().toISOString() })
      .eq('id', ride.id);

    setPhase('dropoff');
  };

  const handleDroppedOff = async () => {
    await supabase
      .from('ride_requests')
      .update({
        status: 'completed' as any,
        completed_at: new Date().toISOString(),
        final_fare: ride.estimated_fare,
      })
      .eq('id', ride.id);

    onComplete();
  };

  const handleCall = () => {
    if (customerPhone) {
      window.open(`tel:${customerPhone}`, '_self');
    } else {
      toast({ title: 'No phone number', description: 'Customer phone is not available yet.' });
    }
  };

  const handleEmergency = () => {
    window.open('tel:112', '_self');
  };

  const handleShareTrip = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Trip in progress',
          text: `I am currently ${phase === 'pickup' ? 'heading to pick up' : 'dropping off'} ${customerName}.`,
        });
      }
    } catch {
      // intentionally silent
    }
  };

  const handleSendMessage = async () => {
    const trimmed = chatDraft.trim();
    if (!trimmed) return;

    const { data: authData } = await supabase.auth.getUser();
    if (!authData.user) return;

    const { data: latestRide } = await supabase
      .from('ride_requests')
      .select('notes')
      .eq('id', ride.id)
      .maybeSingle();

    const nextNoteValue = appendRideChatMessage(latestRide?.notes ?? rawRideNotes, {
      id: crypto.randomUUID(),
      senderId: authData.user.id,
      senderRole: 'driver',
      text: trimmed,
      createdAt: new Date().toISOString(),
    });

    const { error } = await supabase
      .from('ride_requests')
      .update({ notes: nextNoteValue })
      .eq('id', ride.id);

    if (error) {
      toast({ title: 'Message failed', description: 'Please try again.', variant: 'destructive' });
      return;
    }

    setRawRideNotes(nextNoteValue);
    setChatMessages(parseRideChatNotes(nextNoteValue));
    setChatDraft('');
  };

  return (
    <div className="h-full flex flex-col relative bg-background">
      <div ref={mapRef} className="flex-1" />

      <div className="absolute top-0 left-0 right-0 z-[1000] px-4 pt-10 pb-4 bg-card/95 backdrop-blur border-b border-border safe-area-top">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 shadow-md">
            <Navigation className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-bold text-foreground truncate">{currentInstruction}</p>
            <p className="text-sm text-muted-foreground truncate">{phase === 'pickup' ? ride.pickup_address : ride.dropoff_address}</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-foreground">{etaMinutes ?? '—'}</p>
            <p className="text-xs text-muted-foreground">min</p>
          </div>
        </div>
      </div>

      <button
        onClick={() => setShowSafety(true)}
        className="absolute top-32 right-4 z-[1000] w-11 h-11 rounded-full bg-card border border-border shadow-lg flex items-center justify-center"
      >
        <Shield className="w-5 h-5 text-primary" />
      </button>

      {showSafety && (
        <div className="fixed inset-0 z-[3000] flex items-end justify-center">
          <div className="absolute inset-0 bg-foreground/35" onClick={() => setShowSafety(false)} />
          <div className="relative w-full max-w-md bg-card rounded-t-3xl p-6 animate-in slide-in-from-bottom border border-border">
            <div className="w-12 h-1.5 bg-muted rounded-full mx-auto mb-4" />
            <h3 className="text-lg font-bold mb-4 text-center text-foreground">Safety tools</h3>

            <div className="space-y-3">
              <button
                onClick={handleEmergency}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-destructive/10 border border-destructive/30"
              >
                <div className="w-12 h-12 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <p className="font-bold text-foreground">Emergency (112)</p>
                  <p className="text-xs text-muted-foreground">Call emergency services immediately</p>
                </div>
              </button>

              <button
                onClick={handleShareTrip}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted border border-border"
              >
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                  <Share2 className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <p className="font-bold text-foreground">Share trip</p>
                  <p className="text-xs text-muted-foreground">Send your live ride context</p>
                </div>
              </button>

              <button
                onClick={() => { onCancel(); setShowSafety(false); }}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted border border-border"
              >
                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                  <X className="w-6 h-6 text-secondary-foreground" />
                </div>
                <div className="text-left">
                  <p className="font-bold text-foreground">Cancel ride</p>
                  <p className="text-xs text-muted-foreground">Use only if ride cannot continue</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {showChat && (
        <div className="fixed inset-0 z-[3000] flex items-end justify-center">
          <div className="absolute inset-0 bg-foreground/35" onClick={() => setShowChat(false)} />
          <div className="relative w-full max-w-md bg-card rounded-t-3xl border border-border animate-in slide-in-from-bottom">
            <div className="w-12 h-1.5 bg-muted rounded-full mx-auto mt-3 mb-3" />
            <div className="px-4 pb-4">
              <h3 className="text-base font-bold text-foreground mb-3">Chat with {customerName}</h3>
              <div className="h-56 overflow-y-auto rounded-xl bg-muted/40 p-3 space-y-2 mb-3">
                {chatMessages.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No messages yet.</p>
                ) : (
                  chatMessages.map((message) => {
                    const mine = message.senderRole === 'driver';
                    return (
                      <div key={message.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-foreground'}`}>
                          {message.text}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  value={chatDraft}
                  onChange={(event) => setChatDraft(event.target.value)}
                  placeholder="Type a message"
                  className="h-10"
                />
                <Button onClick={handleSendMessage} className="h-10 px-4">Send</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 z-[1000] bg-card rounded-t-3xl shadow-2xl border-t border-border">
        <div className="w-12 h-1.5 bg-muted rounded-full mx-auto mt-3 mb-2" />
        <div className="px-5 pb-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <p className="text-sm font-semibold text-foreground">
              {phase === 'pickup' ? `Picking up ${customerName}` : `Dropping off ${customerName}`}
            </p>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-muted flex items-center justify-center flex-shrink-0">
              {customerAvatar ? (
                <img src={customerAvatar} alt={customerName} className="w-full h-full object-cover" />
              ) : (
                <User className="w-6 h-6 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-foreground truncate">{customerName}</p>
              <div className="flex items-center gap-2">
                <MapPin className="w-3 h-3 text-muted-foreground" />
                <p className="text-xs text-muted-foreground truncate">
                  {phase === 'pickup' ? ride.pickup_address : ride.dropoff_address}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={handleCall}
                className="w-10 h-10 rounded-full bg-muted border border-border flex items-center justify-center"
              >
                <Phone className="w-4 h-4 text-foreground" />
              </button>
              <button
                onClick={() => setShowChat(true)}
                className="w-10 h-10 rounded-full bg-muted border border-border flex items-center justify-center"
              >
                <MessageSquare className="w-4 h-4 text-foreground" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 items-center bg-muted/50 rounded-xl px-3 py-3 mb-4">
            <div className="text-center">
              <p className="text-lg font-bold text-foreground">{etaMinutes ?? '—'} min</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">ETA</p>
            </div>
            <div className="text-center border-x border-border">
              <p className="text-lg font-bold text-foreground">{distanceText}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Distance</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-foreground">D{ride.estimated_fare.toFixed(0)}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Fare</p>
            </div>
          </div>

          {phase === 'pickup' ? (
            <div className="flex gap-3">
              <Button variant="outline" onClick={onCancel} className="h-12 px-5 rounded-xl">
                <X className="w-5 h-5" />
              </Button>
              <Button onClick={handlePickedUp} className="flex-1 h-12 font-bold rounded-xl">
                Confirm pickup
              </Button>
            </div>
          ) : (
            <Button onClick={handleDroppedOff} className="w-full h-12 font-bold rounded-xl">
              Complete drop-off
            </Button>
          )}
        </div>
      </div>

      <button
        className="absolute left-1/2 -translate-x-1/2 bottom-[330px] z-[1000] w-12 h-12 rounded-full border border-border bg-card shadow-lg flex items-center justify-center"
        onClick={() => mapInstanceRef.current?.setView([targetCoords.lat, targetCoords.lng], 16)}
      >
        <ChevronRight className="w-5 h-5 text-foreground rotate-90" />
      </button>
    </div>
  );
};

export default ActiveRideNavigation;
